"use client";

import { Box, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import PageLoading from "@/components/core/page-loading";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import CustomBreadcrumbs from "@/main/category/(components)/custom-breadcrumbs";
import { IHeaderCategories, IGrandChild, IHeaderMenuData } from "@/types/header";
import { decode } from "html-entities";
import { languageIds } from "@/lib/language";
import { getRequest } from "@/utils/request";
import { ICategorySimple, IInformationData } from "@/types/sitemap";
import Link from "@/components/core/localized-link";

const SiteMapPage = ({ lang }: { lang: string }) => {
  const [informationData, setInformationData] = useState<IInformationData | null>(null);
  const [menuData, setMenuData] = useState<IHeaderMenuData | null>(null);
  const [categories, setCategories] = useState<ICategorySimple[] | null>(null);
  const [isInformationLoading, setIsInformationLoading] = useState<boolean>(true);
  const [isMenuLoading, setIsMenuLoading] = useState<boolean>(true);
  const [isInformationError, setIsInformationError] = useState<boolean>(false);
  const [isMenuError, setIsMenuError] = useState<boolean>(false);
  const links = [
    { href: "/special", text: t`Specials` },
    {
      href: "/account",
      text: t`My Account`,
      children: [
        { href: "/account/edit-account", text: t`Account Information` },
        { href: "/account/password", text: t`Password` },
        { href: "/account/addresses", text: t`Address Book` },
        { href: "/account/orders", text: t`Order History` },
      ],
    },
    { href: "/cart", text: t`View cart` },
    { href: "/checkout", text: t`Checkout` },
    { href: "/search", text: t`Enter search term` },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setIsInformationLoading(true);
      setIsMenuLoading(true);
      setIsInformationError(false);
      setIsMenuError(false);

      try {
        const response = await getRequest({ path: { name: "information" }, lang });
        setInformationData(response.data);
        const menuResponse = await getRequest({
          path: { name: "headerMenu" },
          lang,
        });
        setMenuData(menuResponse.data);
        const categoriesResponse = await getRequest({
          path: { name: "category" },
          lang,
        });
        setCategories(categoriesResponse.data.items);
      } catch (error) {
        if (error instanceof Error) {
          if (error.message.includes("information")) {
            setIsInformationError(true);
          }
          if (error.message.includes("menu")) {
            setIsMenuError(true);
          }
        } else {
          console.error("Unknown error:", error);
        }
      } finally {
        setIsInformationLoading(false);
        setIsMenuLoading(false);
      }
    };

    fetchData();
  }, [lang]);

  const combineCategories = (
    menuCategories: IHeaderCategories[],
    categories: ICategorySimple[],
  ) => {
    const displayedNames = new Set<string>();
    const combinedCategories: ICategorySimple[] = [];

    const addUniqueCategories = (sourceCategories: (IHeaderCategories | ICategorySimple)[]) => {
      sourceCategories.forEach((category) => {
        if (!displayedNames.has(category.name)) {
          displayedNames.add(decode(category.name));
          combinedCategories.push(category);
        }
      });
    };

    // Add categories from menuCategories and categories
    addUniqueCategories(menuCategories);
    addUniqueCategories(categories);

    // Sort combined categories alphabetically
    combinedCategories.sort((a, b) => a.name.localeCompare(b.name));

    return combinedCategories;
  };

  const renderInformation = (items: any[]) => {
    return (
      <ul style={{ listStyleType: "disc", paddingLeft: "20px" }}>
        <li>
          <span>
            <Trans>Information</Trans>
          </span>
          {items.map((item) => {
            const seoUrl = item.seo_url[languageIds[lang]];
            return (
              <ul key={item.id} style={{ listStyleType: "circle", paddingLeft: "20px" }}>
                <li>
                  <Link
                    href={`/information/${seoUrl}`}
                    className="underline-none duration-300 ease-in hover:text-[#0097ab]"
                  >
                    {decode(item.title)}
                  </Link>
                </li>
              </ul>
            );
          })}
          <ul style={{ listStyleType: "circle", paddingLeft: "20px" }}>
            <li>
              <a
                href="https://help.offerz.ch/hc/en-ch"
                className="underline-none duration-300 ease-in hover:text-[#0097ab]"
              >
                <Trans>Contact Us</Trans>
              </a>
            </li>
          </ul>
        </li>
      </ul>
    );
  };
  const renderCategoryLinks = (categories: any[]) => {
    return (
      <ul style={{ listStyleType: "disc", paddingLeft: "20px" }}>
        {categories.map((category) => (
          <li key={category.id}>
            {category.href ? (
              <Link
                href={`/category/${category.href.split("/").slice(-1)}`}
                className="underline-none duration-300 ease-in hover:text-[#0097ab]"
              >
                {decode(category.name)}
              </Link>
            ) : (
              <Link
                href={`/category/${category.seo_url}`}
                className="underline-none duration-300 ease-in hover:text-[#0097ab]"
              >
                {decode(category.name)}
              </Link>
            )}
            {category.children && (
              <ul style={{ listStyleType: "circle", paddingLeft: "20px" }}>
                {category.children.map(
                  (child: {
                    id: React.Key | null | undefined;
                    href: string | undefined;
                    name: string | undefined;
                    grand_childs: IGrandChild[];
                  }) => (
                    <li key={child.id}>
                      {child.href ? (
                        <Link
                          href={`/category/${child.href.split("/").slice(-1)}`}
                          className="underline-none duration-300 ease-in hover:text-[#0097ab]"
                        >
                          {decode(child.name)}
                        </Link>
                      ) : (
                        <p>{decode(child.name)}</p>
                      )}

                      {child.grand_childs && (
                        <ul style={{ listStyleType: "square", paddingLeft: "20px" }}>
                          {child.grand_childs.map((grandchild) => (
                            <li key={grandchild.id}>
                              {grandchild.href ? (
                                <Link
                                  href={`/category/${grandchild.href.split("/").slice(-1)}`}
                                  className="underline-none duration-300 ease-in hover:text-[#0097ab]"
                                >
                                  {decode(grandchild.name)}
                                </Link>
                              ) : (
                                <p>{decode(grandchild.name)}</p>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ),
                )}
              </ul>
            )}
          </li>
        ))}
      </ul>
    );
  };
  const renderLinks = (links: any[]) => {
    return (
      <ul style={{ listStyleType: "disc", paddingLeft: "20px" }}>
        {links.map((link, index) => (
          <li key={index}>
            {link.href ? (
              <Link
                href={link.href}
                className="underline-none duration-300 ease-in hover:text-[#0097ab]"
              >
                {decode(link.text)}
              </Link>
            ) : (
              <p>{decode(link.text)}</p>
            )}
            {link.children && (
              <ul style={{ listStyleType: "circle", paddingLeft: "20px" }}>
                {link.children.map(
                  (
                    child: {
                      href: string | undefined;
                      text: string | undefined;
                    },
                    idx: React.Key | null | undefined,
                  ) => (
                    <li key={idx}>
                      {child.href ? (
                        <Link
                          href={child.href}
                          className="underline-none duration-300 ease-in hover:text-[#0097ab]"
                        >
                          {decode(child.text)}
                        </Link>
                      ) : (
                        <p>{decode(child.text)}</p>
                      )}
                    </li>
                  ),
                )}
              </ul>
            )}
          </li>
        ))}
      </ul>
    );
  };

  const combinedCategories =
    menuData && categories
      ? combineCategories(menuData.categories, categories)
      : categories || menuData?.categories || [];

  return (
    <main className="md:container">
      <div className="flex flex-col xs:pt-[75px] md:pt-0">
        <div className="w-full">
          <ul className="pb-2.5 xs:px-[15px] md:px-0 md:pb-[25px]">
            <CustomBreadcrumbs crumbs={[{ title: t`Site Map`, link: "/sitemap" }]} />
          </ul>
        </div>
        <Box sx={{ mb: "20px", padding: { xs: "15px", md: 0 } }}>
          <Typography
            sx={{
              fontSize: "21px",
              fontWeight: "bold",
              letterSpacing: "-0.2px",
              lineHeight: "1.1",
              mb: "20px",
            }}
          >
            <Trans>Site Map</Trans>
          </Typography>
          {isMenuLoading || isInformationLoading ? (
            <PageLoading />
          ) : isMenuError || isInformationError ? (
            <Typography>
              <Trans>Error loading links</Trans>
            </Typography>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 0, sm: 4 },
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ flex: 1 }}>
                {combinedCategories && renderCategoryLinks(combinedCategories)}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box>
                  {renderLinks(links)}
                  {informationData?.items && renderInformation(informationData?.items)}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </div>
    </main>
  );
};

export default SiteMapPage;
